import {
  DeleteIcon,
  DownloadIcon,
  ExternalLinkIcon,
  HamburgerIcon
} from "@chakra-ui/icons";
import { IconButton, Link, Tooltip } from "@chakra-ui/react";
import { RowStatus } from "components/dynamic-table/row-status";
import { openModalConfirm } from "components/modal-confirm-new";
import { useEffect, useMemo, useRef } from "react";
import { usePromiseData } from "hooks/use-promise-data";
import api from "api/api";
import { makeEvent, useEventListener } from "services/events";
import { openMailingOptions } from "../mailing-options-modal";
import { IconButtonWithLoading } from "./icon-button-with-loading";
import { downloadMailing } from "./functions";
import {
  MailingTableRowDetails,
  openLegacyPipeline
} from "./mailing-table-row-details";
import { dropdownConvenioAtendimento } from "components/atendimentos-components/atendimento-form/fields-data";
import { isColomboUser } from "utils/is-colombo-user";
import { useApplicationContext } from "contexts/ApplicationContext";
import {
  TipoOperacaoMailing,
  dropdownTipoOperacaoMailing
} from "../mailing-upload-modal/consts";
import { Convenio } from "components/atendimentos-components/atendimento-form/types";
import { Toast } from "components/toast";
import { mailingConveniosOptions } from "../mailing-modal-filter/consts";
import {
  CustomTableColor,
  CustomTableColorColumn
} from "components/dynamic-table/color-table";
import { useRouter } from "router-manager/parts";
import { StatesMachineProps } from "components/pipeline-components/classes/state-machine/state-machine";
import { apiJWT } from "api/api-jwt";

export interface MailingTableRow {
  dataCriacao: string;
  name: string;
  id: number;
  qtdSolicitada: number;
  qtdGerada: number;
  status:
    | "PENDING_GENERATION"
    | "PENDING_INSERT"
    | "FINISHED"
    | "ERROR_GENERATION"
    | "ERROR_INSERT";
  subConvenio: string;
  currentPipelineOrdem: null | number;
  filtrosJson: "[]";
  origem?:
    | "API_NOVA_VENDAS"
    | "MANUAL"
    | "PIPELINE"
    | "UPLOAD_MANUAL"
    | "API_MULTICORBAN";
  tipoOperacao: TipoOperacaoMailing;
  convenio: Convenio | "CONSIGNADO_TRABALHADOR";
  bancosFrom: number[];
  bancosTo: number[];
  digitacaoLote: boolean;
  userId: number;
  customerId: number;
  pipeline: null | StatesMachineProps;
}

export function useMailingsData() {
  const {
    isLoading,
    result: mailings,
    onUpdate,
    setResult,
    currentPage,
    nextPage,
    prevPage,
    setPage
  } = usePromiseData<{ mailings: MailingTableRow[]; total: number }>(
    async (pageNumber) => {
      const { data } = await apiJWT.get("/mailings", {
        params: { page_size: "10", page: pageNumber }
      });
      return data;
    }
  );

  const mailingsRows = useMemo(() => mailings?.mailings ?? [], [mailings]);
  const total = mailings?.total ?? 0;

  useEventListener("update-mailings-table", onUpdate);

  const deleteMailing = async (id: number) => {
    try {
      await api.delete(`/mailings/${id}`);
      setResult((prev) =>
        prev
          ? {
              ...prev,
              mailings: prev.mailings.filter(
                (currMailing) => currMailing.id !== id
              )
            }
          : prev
      );
    } catch (e) {
      Toast({
        title: "Ocorreu um erro ao deletar mailing"
      });
    }
  };

  useEffect(() => {
    const interval = setTimeout(() => {
      if (mailingsRows.find((curr) => curr.status.includes("PENDING")))
        onUpdate();
      else setResult((prev) => (prev ? { ...prev } : prev));
    }, 15000);
    return () => clearTimeout(interval);
  }, [mailingsRows, onUpdate, setResult]);

  return {
    isLoading,
    mailings: mailingsRows,
    total,
    deleteMailing,
    currentPage,
    nextPage,
    prevPage,
    setPage
  };
}

export function MailingsTable({
  isLoading,
  mailings,
  total,
  deleteMailing,
  currentPage,
  nextPage,
  prevPage,
  setPage
}: ReturnType<typeof useMailingsData>) {
  const downloadRef = useRef<HTMLAnchorElement>(null);

  const { user } = useApplicationContext();
  const visaoUsuarioExpecifico = user.userData.customerId === 1692;
  const router = useRouter();

  const columns: CustomTableColorColumn<MailingTableRow>[] = [
    {
      name: "Data Criação",
      render: ({ dataCriacao }) => new Date(dataCriacao).toLocaleString()
    },
    {
      name: "Convênio",
      render: (row) => {
        const convenio = [
          ...dropdownConvenioAtendimento,
          ...mailingConveniosOptions
        ].find((curr) => curr.value === row.convenio)?.name;
        return <>{convenio ?? row.convenio}</>;
      }
    },
    {
      name: "Tipo Operação",
      render: (row) => {
        return (
          dropdownTipoOperacaoMailing.find(
            (curr) => curr.value === row.tipoOperacao
          )?.name || ""
        );
      }
    },
    {
      name: "Nome",
      render(row) {
        return row.name;
      }
    },
    {
      name: "Solicitada",
      render(row) {
        return row.qtdSolicitada;
      }
    },
    {
      name: "Gerada",
      render: ({ qtdGerada }) => qtdGerada
    },
    {
      name: "Status (Geração)",
      render: ({ status }) => {
        const parse: any = {
          PENDING_GENERATION: "Pendente",
          PENDING_INSERT: "Processando",
          FINISHED: "Concluído",
          ERROR_GENERATION: "Erro gerando Mailing",
          ERROR_INSERT: "Erro inserindo Mailing"
        };
        return (
          <RowStatus status={status} size="sm">
            {parse[status]}
          </RowStatus>
        );
      }
    },
    {
      name: "Ações",
      render: ({ id, name, status, origem, ...rest }) => {
        return (
          <>
            <Tooltip label="Configurar Pipeline">
              <IconButton
                mr="8px"
                icon={<ExternalLinkIcon />}
                aria-label=""
                onClick={() => {
                  router.push({
                    pathname: `/mailing/pipeline/${id}`,
                    payload: { id, name, status, origem, ...rest }
                  });
                }}
                isDisabled={
                  status !== "FINISHED" ||
                  rest.currentPipelineOrdem !== null ||
                  rest.pipeline != null
                }
              />
            </Tooltip>
            {/* {!isColomboUser(user) || visaoUsuarioExpecifico ? (
              <Tooltip label="Opções Mailing">
                <IconButton
                  mr="8px"
                  icon={<HamburgerIcon width="20px" height="20px" />}
                  aria-label=""
                  onClick={() => {
                    openMailingOptions({ id, name, status, ...rest });
                  }}
                  isDisabled={
                    status !== "FINISHED" ||
                    rest.currentPipelineOrdem !== null ||
                    rest.convenio === "CONSIGNADO_TRABALHADOR" ||
                    rest.pipeline != null
                  }
                />
              </Tooltip>
            ) : null} */}

            <IconButtonWithLoading
              mr="8px"
              tooltipMessage="Download"
              icon={<DownloadIcon />}
              aria-label=""
              onClick={async () => {
                downloadMailing(id, name, downloadRef);
              }}
              isDisabled={status !== "FINISHED" || origem === "PIPELINE"}
            />

            <Tooltip label="Excluir">
              <IconButton
                icon={<DeleteIcon />}
                variant="danger"
                aria-label=""
                onClick={() => {
                  openModalConfirm({
                    onConfirm: async () => deleteMailing(id),
                    message: "Deseja excluir o mailing?",
                    confirmButtonStyle: {
                      children: "Sim",
                      leftIcon: <DeleteIcon />
                    }
                  });
                }}
              />
            </Tooltip>
          </>
        );
      }
    }
  ];

  return (
    <>
      <Link display="none" ref={downloadRef} />
      <CustomTableColor
        columns={columns}
        rows={mailings ?? []}
        isLoading={isLoading}
        InnerRowRender={MailingTableRowDetails}
        onClickRow={(row, i) => {
          openLegacyPipeline(i);
        }}
        currentPage={currentPage}
        onPrevPage={prevPage}
        onNextPage={nextPage}
        onFirstPage={() => setPage(0)}
        onSetPage={setPage}
        rowsLength={total}
      />
    </>
  );
}

export function updateMailingsTable() {
  return makeEvent("update-mailings-table");
}
